import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { usePostRace, useRefetchAll, useTrainingSettings, useUser } from '@utils/hooks/queries';
import MigrationDialog from './MigrationDialog';

/**
 * PRD 12 §7 — raised at **app opening**, not at login: an athlete on a persisted session never logs in
 * again. The condition is `availability_confirmed_at IS NULL`, which is the state the cutover leaves them
 * in — `seedUserDaysUnconfirmed` writes the derived days without stamping the confirmation, precisely so
 * this fires.
 *
 * It yields to the post-race screen: that one asks what the athlete's next objective is, which matters
 * more than confirming the days of a plan they are about to replace. The two never stack.
 */
const EXCLUDED = ['/onboarding', '/new-objective'];

export const MigrationGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { user } = useUser();
  const { settings } = useTrainingSettings();
  const { state: postRace } = usePostRace();
  const { refetchAll } = useRefetchAll();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  if (!isAuthenticated || !user?.plan_v2_id) return null;
  if (EXCLUDED.some((path) => location.pathname.startsWith(path))) return null;
  if (dismissed) return null;
  // The post-race question comes first.
  if (postRace?.due) return null;
  if (!settings || settings.confirmedAt !== null) return null;

  return (
    <MigrationDialog
      onConfirmed={async () => {
        setDismissed(true);
        await refetchAll();
      }}
    />
  );
};

export default MigrationGate;
